/**
 * AI League page data: live scores and per-model list previews for /ai-league.
 * Subscribes to gameUpdates, matchday config, progression awards, and the signed-in user's roster.
 */
import { useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ACTIVE_TOURNAMENT } from '@/constants';
import type { AiModelContent, AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import { FirebaseContext } from '@/session/FirebaseContext';
import { computeListScore, type ComputeListScoreOptions } from '@/helpers/scoring/computeListScore';
import { teamHasList } from '@/helpers/leagues/leagueMemberSync';
import { useAiLeagueModels } from '@/hooks/useAiLeagueModels';
import { useAiLeagueScoringContext } from '@/hooks/useAiLeagueScoringContext';
import type { gameResult, gameUpdate } from '@/models/gameUpdate';
import { queryGameUpdates, sentinelType } from '@/models/gameUpdate';
import type User from '@/models/user';
import type { TeamSelectionRecord, leagueMember } from '@/models/user';
import { toDict, type Dict } from '@/helpers';
import { Timestamp } from 'firebase/firestore';

export interface AiLeagueParticipant {
  id: AiModelId | 'you';
  name: string;
  currentScore: number;
  prevScore: number;
  hasList: boolean;
}

export interface AiLeagueLeaderboardEntry extends AiLeagueParticipant {
  displayRank: number;
  prevDisplayRank: number;
  showRankChange: boolean;
}

const AI_MEMBER_TS = Timestamp.fromDate(new Date('2026-05-01'));

function toLeagueMember(participant: AiLeagueParticipant, rank: number, prevRank: number): leagueMember {
  return {
    name: participant.name,
    currentRank: rank,
    prevRank,
    currentScore: participant.currentScore,
    prevScore: participant.prevScore,
    joinedLeague: AI_MEMBER_TS,
    hasList: participant.hasList,
    spectator: !participant.hasList,
    rankLastUpdated: AI_MEMBER_TS,
    guessError: undefined,
    userID: participant.id,
    latecomer: false,
  };
}

export type AiLeagueUserYouLine =
  | { rank: number; score: number }
  | 'No list yet'
  | 'Tournament not started · 0 pts'
  | null;

export type AiLeagueChartUserInput = {
  id: 'you';
  name: string;
  countryList: TeamSelectionRecord['countryList'];
  listIndexSwapPair?: [number, number] | null;
  mdReset?: number;
};

export function useAiLeaguePageData(gameResults: gameResult[]) {
  const ctx = useContext(FirebaseContext);
  const scoring = useAiLeagueScoringContext();
  const { aiModels } = useAiLeagueModels();
  const [gameUpdates, setGameUpdates] = useState<Dict<gameUpdate>>();
  const [teamSelection, setTeamSelection] = useState<TeamSelectionRecord | null>(null);
  const [userData, setUserData] = useState<User | null>(null);

  useEffect(() => {
    return onSnapshot(queryGameUpdates(ctx.db), (snapshot) => {
      setGameUpdates(toDict(snapshot));
    });
  }, [ctx.db]);

  useEffect(() => {
    if (!ctx.user) {
      setTeamSelection(null);
      setUserData(null);
      return;
    }
    const unsubTeam = onSnapshot(doc(ctx.db, 'users', ctx.user.uid, 'tournaments', ACTIVE_TOURNAMENT), (snapshot) => {
      setTeamSelection(snapshot.exists() ? (snapshot.data() as TeamSelectionRecord) : null);
    });
    const unsubUser = onSnapshot(doc(ctx.db, 'users', ctx.user.uid), (snapshot) => {
      setUserData(snapshot.exists() ? (snapshot.data() as User) : null);
    });
    return () => {
      unsubTeam();
      unsubUser();
    };
  }, [ctx.db, ctx.user]);

  const tournamentStarted = gameUpdates != null && sentinelType.tournamentStarted in gameUpdates;
  const { settledMatchday } = scoring;

  const listScoreBase = useMemo(
    (): Omit<ComputeListScoreOptions, 'atMatchday'> => ({
      progressionAwards: scoring.progressionAwards,
      matchdayCount: scoring.matchdayCount,
      swapMatchday: scoring.swapMatchday,
    }),
    [scoring.matchdayCount, scoring.progressionAwards, scoring.swapMatchday],
  );

  const userDisplayName = useMemo(() => {
    if (!ctx.user) return null;
    const name = userData?.name?.trim();
    if (name) return name;
    const authName = ctx.user.displayName?.trim();
    if (authName) return authName;
    return 'Anonymous';
  }, [ctx.user, userData?.name]);

  const aiParticipants = useMemo((): AiLeagueParticipant[] => {
    return aiModels.map((model) => {
      const rosterOptions = {
        ...listScoreBase,
        listIndexSwapPair: model.listIndexSwapPair ?? null,
        mdReset: 0,
      };
      return {
        id: model.id,
        name: model.displayName,
        currentScore: model.listValid
          ? computeListScore(model.countryList, gameResults, { ...rosterOptions, atMatchday: settledMatchday })
          : 0,
        prevScore: model.listValid
          ? computeListScore(model.countryList, gameResults, {
              ...rosterOptions,
              atMatchday: Math.max(0, settledMatchday - 1),
            })
          : 0,
        hasList: true,
      };
    });
  }, [aiModels, gameResults, listScoreBase, settledMatchday]);

  const youParticipant = useMemo((): AiLeagueParticipant | null => {
    if (!ctx.user || !teamSelection || !teamHasList(teamSelection)) {
      return null;
    }
    const rosterOptions = {
      ...listScoreBase,
      listIndexSwapPair: teamSelection.listIndexSwapPair,
      mdReset: teamSelection.mdReset ?? 0,
    };
    const currentScore = computeListScore(teamSelection.countryList, gameResults, {
      ...rosterOptions,
      atMatchday: settledMatchday,
    });
    const prevScore = computeListScore(teamSelection.countryList, gameResults, {
      ...rosterOptions,
      atMatchday: Math.max(0, settledMatchday - 1),
    });
    return {
      id: 'you',
      name: userDisplayName ?? 'You',
      currentScore: tournamentStarted ? currentScore : 0,
      prevScore: tournamentStarted ? prevScore : 0,
      hasList: true,
    };
  }, [ctx.user, teamSelection, gameResults, listScoreBase, settledMatchday, tournamentStarted, userDisplayName]);

  const leaderboardEntries = useMemo((): AiLeagueLeaderboardEntry[] => {
    const participants: AiLeagueParticipant[] = [...aiParticipants];
    if (youParticipant) {
      participants.push(youParticipant);
    }

    const sorted = [...participants].sort((a, b) => {
      if (a.hasList !== b.hasList) return a.hasList ? -1 : 1;
      return b.currentScore - a.currentScore;
    });

    const prevSorted = [...participants].sort((a, b) => {
      if (a.hasList !== b.hasList) return a.hasList ? -1 : 1;
      return b.prevScore - a.prevScore;
    });

    const prevRankById = new Map(prevSorted.map((participant, index) => [participant.id, index + 1]));

    return sorted.map((participant, index) => ({
      ...participant,
      displayRank: index + 1,
      prevDisplayRank: prevRankById.get(participant.id) ?? index + 1,
      showRankChange: tournamentStarted && settledMatchday > 0 && participant.hasList,
    }));
  }, [aiParticipants, youParticipant, tournamentStarted, settledMatchday]);

  const leader = useMemo(() => {
    const topAi = leaderboardEntries.find((entry) => entry.id !== 'you' && entry.hasList);
    return topAi?.name ?? null;
  }, [leaderboardEntries]);

  const userYouLine = useMemo((): AiLeagueUserYouLine => {
    if (!ctx.user) return null;
    if (!youParticipant) return 'No list yet';
    if (!tournamentStarted) return 'Tournament not started · 0 pts';
    const entry = leaderboardEntries.find((e) => e.id === 'you');
    if (!entry) return 'No list yet';
    return { rank: entry.displayRank, score: entry.currentScore };
  }, [ctx.user, youParticipant, tournamentStarted, leaderboardEntries]);

  const chartUser = useMemo((): AiLeagueChartUserInput | undefined => {
    if (!youParticipant || !teamSelection || !teamHasList(teamSelection)) {
      return undefined;
    }
    return {
      id: 'you',
      name: youParticipant.name,
      countryList: teamSelection.countryList,
      listIndexSwapPair: teamSelection.listIndexSwapPair,
      mdReset: teamSelection.mdReset ?? 0,
    };
  }, [youParticipant, teamSelection]);

  return {
    aiModels,
    tournamentStarted,
    settledMatchday,
    chartUser,
    leaderboardEntries,
    leader,
    userYouLine,
    userDisplayName,
    isLoggedIn: Boolean(ctx.user),
    hasUserList: Boolean(youParticipant),
    getMemberForRow: (entry: AiLeagueLeaderboardEntry) =>
      toLeagueMember(entry, entry.displayRank, entry.prevDisplayRank),
  };
}
